import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, combineLatest, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import {
  getConfiguredProductUuid,
  getConfiguredVariationUuid,
  getMceRedirectProductEnv,
  isMceRedirectProductConfigured,
  MCE_REDIRECT_PRODUCTS,
  MceRedirectProductCode,
  MceRedirectProductIntent,
  normalizeMceRedirectProductCode,
  STORAGE_MCE_REDIRECT_PRODUCT_INTENT,
} from '../constants/mce-redirect-products.constants';
import { CartFacade } from '../store/cart/cart.facade';
import { AvailableOffersFacade } from '../store/available-offers/available-offers.facade';
import { ToastService } from '../../shared/services/toast.service';
import { ProductV2Variation } from '../models/product-v2.model';

import { ProductV2Service } from './product-v2.service';
import { AuthService } from './auth.service';
import { I18nService } from './i18n.service';


export interface MceRedirectProductAddResult {
  handled: boolean;
  success: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MceRedirectProductService {
  private productV2Service = inject(ProductV2Service);
  private cartFacade = inject(CartFacade);
  private availableOffersFacade = inject(AvailableOffersFacade);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private i18nService = inject(I18nService);
  private platformId = inject(PLATFORM_ID);


  saveIntent(code: MceRedirectProductCode): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const quantity = MCE_REDIRECT_PRODUCTS[code]?.quantity ?? 1;
    const intent: MceRedirectProductIntent = { code, quantity };

    try {
      sessionStorage.setItem(STORAGE_MCE_REDIRECT_PRODUCT_INTENT, JSON.stringify(intent));
    } catch {
      // ignore
    }
  }

  getIntent(): MceRedirectProductIntent | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const raw = sessionStorage.getItem(STORAGE_MCE_REDIRECT_PRODUCT_INTENT);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as Partial<MceRedirectProductIntent>;
      const code = normalizeMceRedirectProductCode(parsed?.code);
      if (!code) {
        return null;
      }

      return {
        code,
        quantity: parsed.quantity && parsed.quantity > 0 ? parsed.quantity : 1,
      };
    } catch {
      return null;
    }
  }

  clearIntent(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      sessionStorage.removeItem(STORAGE_MCE_REDIRECT_PRODUCT_INTENT);
    } catch {
      // ignore
    }
  }

  tryAddFromStoredIntent(showToasts = true): Observable<MceRedirectProductAddResult> {
    const intent = this.getIntent();

    if (!intent) {
      return of({ handled: false, success: false });
    }

    return this.addProductByCode(intent.code, intent.quantity, showToasts).pipe(
      map(success => {
        this.clearIntent();
        return { handled: true, success };
      })
    );
  }

  addProductByCode(
    code: MceRedirectProductCode,
    quantity?: number,
    showToasts = true
  ): Observable<boolean> {
    const env = getMceRedirectProductEnv();

    if (!isMceRedirectProductConfigured(code, env)) {
      if (showToasts) {
        this.toastService.showError(
          this.i18nService.instant('pages.checkout.messages.mceRedirectProductNotConfigured'),
          5000,
          'top-center'
        );
      }
      return of(false);
    }

    const variationUuid = getConfiguredVariationUuid(code, env)!;
    const productUuid = getConfiguredProductUuid(code, env)!;
    const qty = quantity ?? MCE_REDIRECT_PRODUCTS[code].quantity ?? 1;

    return this.addProductByUuid(productUuid, variationUuid, qty, showToasts);
  }

  private addProductByUuid(
    productUuid: string,
    variationUuid: string,
    quantity: number,
    showToasts: boolean
  ): Observable<boolean> {
    return this.productV2Service.getProductByUuid(productUuid).pipe(
      switchMap(response => {
        if (response.error || !response.data) {
          if (showToasts) {
            this.toastService.showError(
              this.i18nService.instant('pages.checkout.messages.mceRedirectProductUnavailable'),
              5000,
              'top-center'
            );
          }
          return of(false);
        }

        const product = response.data;
        this.availableOffersFacade.mapProductVariationsToOffers(product);

        const selectedVariation = product.variations?.find(
          (v: ProductV2Variation) => v.uuid === variationUuid
        );

        if (!selectedVariation) {
          if (showToasts) {
            this.toastService.showError(
              this.i18nService.instant('errors.urlCart.variationNotFound', {
                uuid: variationUuid,
              }),
              5000,
              'top-center'
            );
          }
          return of(false);
        }

        return this.waitForCartStability().pipe(
          switchMap(() =>
            combineLatest([
              this.cartFacade.hasItemInCart(product, selectedVariation),
              this.cartFacade.getItemQuantityInCart(product, selectedVariation),
            ])
          ),
          take(1),
          map(([exists, currentQuantity]) => {
            if (exists) {
              const itemId = this.cartFacade.getItemId(product, selectedVariation);
              this.cartFacade.updateCartItemQuantity(itemId, currentQuantity + quantity);
            } else {
              this.cartFacade.addToCart(product, quantity, selectedVariation, {
                suppressSuccessToast: true,
              });
            }
            this.authService.markCartAsModified();
            return true;
          })
        );
      }),
      catchError(() => {
        if (showToasts) {
          this.toastService.showError(
            this.i18nService.instant('pages.checkout.messages.mceRedirectProductUnavailable'),
            5000,
            'top-center'
          );
        }
        return of(false);
      })
    );
  }

  private waitForCartStability(): Observable<unknown> {
    return this.cartFacade.getCartItems().pipe(
      filter(items => items !== null),
      take(1),
      switchMap(() => timer(300))
    );
  }
}
